import { c as setCurrentOrgId, i as getToken, r as getCurrentOrgId } from "./credentials-CdxijtI9.js";
import { t as fetchOrgs } from "./api-GOhAsGMy.js";
import * as p from "@clack/prompts";
//#region src/commands/auth/orgs.ts
var OrgSelectionCancelledError = class extends Error {
	constructor() {
		super("Organization selection cancelled");
		this.name = "OrgSelectionCancelledError";
	}
};
function isInteractive() {
	return Boolean(process.stdin.isTTY && process.stdout.isTTY) && !process.env.CI;
}
/**
* Resolve the current org, auto-selecting if only one exists.
* If multiple orgs exist and none is currently set, prompts the user.
* Pass `{ forcePrompt: true }` to always prompt when there's more than one
* org, even if a current org is persisted. In non-interactive environments,
* a persisted current org is reused and otherwise a clear setup error is
* thrown instead of starting a prompt that cannot be answered.
*/
async function resolveCurrentOrg(token, opts = {}) {
	const orgs = await fetchOrgs(token);
	if (orgs.length === 0) throw new Error("No organizations found.");
	if (orgs.length === 1) return {
		orgId: orgs[0].id,
		orgName: orgs[0].name
	};
	const envOrgId = process.env.MASTRA_ORG_ID;
	if (envOrgId) {
		const match = orgs.find((o) => o.id === envOrgId);
		if (match) return {
			orgId: match.id,
			orgName: match.name
		};
	}
	const currentOrgId = await getCurrentOrgId();
	if (currentOrgId && (!opts.forcePrompt || !isInteractive())) {
		const match = orgs.find((o) => o.id === currentOrgId);
		if (match) return {
			orgId: match.id,
			orgName: match.name
		};
	}
	if (!isInteractive()) throw new Error("Multiple organizations found. Run `mastra auth orgs switch` interactively or set MASTRA_ORG_ID.");
	let selected;
	try {
		selected = await p.select({
			message: "Select an organization",
			initialValue: currentOrgId ?? void 0,
			options: orgs.map((o) => ({
				value: o.id,
				label: `${o.name}${o.id === currentOrgId ? " (current)" : ""}`,
				hint: o.id
			})),
			...opts.signal ? { signal: opts.signal } : {}
		});
	} catch (error) {
		if (opts.signal?.aborted && opts.exitOnCancel === false) throw new OrgSelectionCancelledError();
		throw error;
	}
	if (p.isCancel(selected)) {
		if (opts.exitOnCancel === false) throw new OrgSelectionCancelledError();
		p.cancel("Cancelled.");
		process.exit(0);
	}
	const org = orgs.find((o) => o.id === selected);
	if (org.id !== currentOrgId) await setCurrentOrgId(org.id);
	return {
		orgId: org.id,
		orgName: org.name
	};
}
async function listOrgsAction() {
	const token = await getToken();
	const currentOrgId = await getCurrentOrgId();
	const orgs = await fetchOrgs(token);
	if (orgs.length === 0) {
		console.info("\nNo organizations found.\n");
		return;
	}
	console.info("\nOrganizations:\n");
	for (const org of orgs) {
		const marker = org.id === currentOrgId ? " (current)" : "";
		const role = org.role ? ` [${org.role}]` : "";
		console.info(`  ${org.name}${role}${marker}`);
		console.info(`    ID: ${org.id}`);
	}
	console.info("");
}
async function switchOrgAction() {
	if (process.env.MASTRA_API_TOKEN) {
		console.error("\nCannot switch org when using MASTRA_API_TOKEN. Unset it and log in with: mastra auth login\n");
		process.exit(1);
	}
	if (process.env.MASTRA_ORG_ID) {
		console.error("\nCannot switch org when MASTRA_ORG_ID is set. Unset it to use persistent org selection.\n");
		process.exit(1);
	}
	const token = await getToken();
	const currentOrgId = await getCurrentOrgId();
	const orgs = await fetchOrgs(token);
	if (orgs.length === 0) {
		console.info("\nNo organizations found.\n");
		return;
	}
	if (orgs.length === 1) {
		console.info(`\nYou only have one organization: ${orgs[0].name}\n`);
		return;
	}
	const selected = await p.select({
		message: "Switch to organization",
		options: orgs.map((o) => ({
			value: o.id,
			label: `${o.name}${o.id === currentOrgId ? " (current)" : ""}`
		}))
	});
	if (p.isCancel(selected)) {
		p.cancel("Cancelled.");
		process.exit(0);
	}
	await setCurrentOrgId(selected);
	const org = orgs.find((o) => o.id === selected);
	console.info(`\nSwitched to ${org.name} (${org.id})\n`);
}
//#endregion
export { switchOrgAction as i, listOrgsAction as n, resolveCurrentOrg as r, OrgSelectionCancelledError as t };

//# sourceMappingURL=orgs-Dj0nij3E.js.map